/**
 * 多裝置雲端同步（個人專案、單一使用者、無登入）。
 *
 * 機制：使用者第一次開啟自動產生一個「同步碼」(syncId)；把同步碼輸入到其他裝置，
 * 三台裝置就共用同一份 watchlist / 投資組合 / 購買力設定。
 *
 * 後端是 last-write-wins 的 blob store（/api/prefs，key=prefs:{syncId}），但 PUT 支援
 * optimistic concurrency（帶 baseServerUpdatedAt，不符回 409）。前端策略：
 *   - 載入時 GET 雲端；若雲端較新且內容不同 → 寫回 localStorage 並 reload 一次。
 *   - 之後每 2 秒比對本機 6 個 key 的快照，有變動就 debounce 後帶 base 版本 PUT 上雲。
 *   - PUT 撞 409（別台先寫入）→ 用「本機目前快照 vs 上次推送快照」做欄位級合併，
 *     保留本機真正改過的 key、其餘採雲端值，再以雲端版本為新 base 重試（避免 lost update）。
 *   - 另每 ~8 秒對 idle 裝置做一次遠端輪詢，拉別台的變更（reload 一次套用）。
 */

import { useEffect, useRef, useState } from 'react';
import { apiUrl, fetchJSON } from './api-client';
import { mergePayloads, payloadsDiffer, type PrefsValues } from './prefs-merge';
import { PORTFOLIO_KEY } from './usePortfolio';
import { BUYING_POWER_STORAGE_KEY } from '../components/BuyingPowerSettings';

const SYNC_ID_KEY = 'invest-copilot.syncId';
const LOCAL_UPDATED_AT_KEY = 'invest-copilot.prefs.updatedAt';
const RELOAD_GUARD_KEY = 'invest-copilot.prefs.reloadedThisSession';

/** 要同步的 localStorage keys（字面值穩定；改動處請同步維護）。 */
const SYNCED_KEYS = [
  'invest-copilot.watchlist.v2',
  'invest-copilot.active.v2',
  'invest-copilot.market.v1',
  'invest-copilot.active-per-market.v1',
  PORTFOLIO_KEY,
  BUYING_POWER_STORAGE_KEY,
] as const;

const SYNC_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

export type SyncStatus = 'off' | 'syncing' | 'synced' | 'error';

function generateSyncId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID().replace(/-/g, '');
    }
  } catch {/**/}
  return `pg${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function readLocalStorage(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLocalStorage(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {/**/}
}

/** 收集要同步的 6 個 key 成 payload（只含實際存在的 key）。 */
function snapshotPayload(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of SYNCED_KEYS) {
    const raw = readLocalStorage(key);
    if (raw == null) continue;
    try { out[key] = JSON.parse(raw); }
    catch { out[key] = raw; }
  }
  return out;
}

function snapshotString(): string {
  return JSON.stringify(snapshotPayload());
}

/** 把雲端 payload 寫回 localStorage；回傳是否真的有改到任何 key。 */
function applyPayload(payload: Record<string, unknown> | null | undefined): boolean {
  if (!payload || typeof payload !== 'object') return false;
  let changed = false;
  for (const key of SYNCED_KEYS) {
    if (!(key in payload)) continue;
    const next = typeof payload[key] === 'string' ? payload[key] as string : JSON.stringify(payload[key]);
    if (readLocalStorage(key) !== next) {
      writeLocalStorage(key, next);
      changed = true;
    }
  }
  return changed;
}

interface PrefsGetResp {
  exists: boolean;
  payload?: Record<string, unknown>;
  serverUpdatedAt?: string;
}
interface PrefsConflictResp {
  conflict: true;
  exists: true;
  payload: Record<string, unknown>;
  clientUpdatedAt?: string;
  serverUpdatedAt: string;
}

type PutResult =
  | { kind: 'ok'; serverUpdatedAt: string }
  | { kind: 'conflict'; cloud: PrefsConflictResp };

/** PUT prefs，回傳 ok / conflict（409 不丟例外，其他非 2xx 才丟）。 */
async function putPrefs(id: string, payload: PrefsValues, baseServerUpdatedAt: string | null): Promise<PutResult> {
  const res = await fetch(apiUrl(`/api/prefs?id=${encodeURIComponent(id)}`), {
    method: 'PUT',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payload,
      clientUpdatedAt: new Date().toISOString(),
      ...(baseServerUpdatedAt ? { baseServerUpdatedAt } : {}),
    }),
  });
  if (res.status === 409) {
    const cloud = (await res.json()) as PrefsConflictResp;
    return { kind: 'conflict', cloud };
  }
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch {/**/}
    throw new Error(`HTTP ${res.status}${body ? `: ${body}` : ''}`);
  }
  const ok = (await res.json()) as { saved: boolean; serverUpdatedAt: string };
  return { kind: 'ok', serverUpdatedAt: ok.serverUpdatedAt };
}

export function getStoredSyncId(): string | null {
  const id = readLocalStorage(SYNC_ID_KEY);
  return id && SYNC_ID_RE.test(id) ? id : null;
}

export interface PrefsSync {
  syncId: string | null;
  status: SyncStatus;
  lastSyncedAt: string | null;
  /** 在其他裝置輸入同步碼後呼叫：驗證 → 寫入 → reload 以套用雲端資料。 */
  applySyncId: (id: string) => boolean;
  /** 第一次啟用同步（自動產生同步碼並把目前本機資料推上雲）。 */
  enableSync: () => void;
}

export function usePrefsSync(): PrefsSync {
  const [syncId, setSyncId] = useState<string | null>(() => getStoredSyncId());
  const [status, setStatus] = useState<SyncStatus>(() => (getStoredSyncId() ? 'syncing' : 'off'));
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const lastPushed = useRef<string>('');
  const baseServerUpdatedAt = useRef<string | null>(readLocalStorage(LOCAL_UPDATED_AT_KEY));
  const scheduledFor = useRef<string>('');
  const hydrated = useRef(false);
  const inFlight = useRef(false);
  const pushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 觸發一次性 reload（套用雲端帶進來的變更），用 sessionStorage guard 防迴圈。 */
  function reloadOnce() {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    window.location.reload();
  }

  async function pushNow(reason: string) {
    const id = getStoredSyncId();
    if (!id || inFlight.current) return;
    inFlight.current = true;
    try {
      setStatus('syncing');
      let snapshot = snapshotPayload();
      let base = baseServerUpdatedAt.current;
      let needReload = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await putPrefs(id, snapshot, base);
        if (result.kind === 'ok') {
          lastPushed.current = JSON.stringify(snapshot);
          baseServerUpdatedAt.current = result.serverUpdatedAt;
          writeLocalStorage(LOCAL_UPDATED_AT_KEY, result.serverUpdatedAt);
          setLastSyncedAt(result.serverUpdatedAt);
          setStatus('synced');
          if (needReload) reloadOnce();
          return;
        }
        // 409 衝突：欄位級合併（本機改過的 key 保留，其餘採雲端），再以雲端版本為新 base 重試。
        const cloud = (result.cloud.payload ?? {}) as PrefsValues;
        const localBase = JSON.parse(lastPushed.current || '{}') as PrefsValues;
        const merged = mergePayloads(cloud, snapshot, localBase, SYNCED_KEYS);
        if (applyPayload(merged)) needReload = true; // 雲端把別台的 key 帶進本機 localStorage
        baseServerUpdatedAt.current = result.cloud.serverUpdatedAt;
        writeLocalStorage(LOCAL_UPDATED_AT_KEY, result.cloud.serverUpdatedAt);
        snapshot = merged;
        base = result.cloud.serverUpdatedAt;
      }
      console.warn('[prefs-sync] push gave up after retries', reason);
      setStatus('error');
    } catch (err) {
      console.warn('[prefs-sync] push failed', reason, err);
      setStatus('error');
    } finally {
      inFlight.current = false;
    }
  }

  // 初次載入：拉雲端，必要時 hydrate + reload
  useEffect(() => {
    const id = getStoredSyncId();
    if (!id) { setStatus('off'); return; }
    let cancelled = false;
    (async () => {
      try {
        const cloud = await fetchJSON<PrefsGetResp>(`/api/prefs?id=${encodeURIComponent(id)}`);
        if (cancelled) return;
        const localUpdatedAt = readLocalStorage(LOCAL_UPDATED_AT_KEY);
        const alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
        if (cloud.exists && cloud.payload && cloud.serverUpdatedAt !== localUpdatedAt) {
          const willChange = applyPayload(cloud.payload);
          baseServerUpdatedAt.current = cloud.serverUpdatedAt ?? null;
          writeLocalStorage(LOCAL_UPDATED_AT_KEY, cloud.serverUpdatedAt ?? '');
          if (willChange && !alreadyReloaded) {
            sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
            window.location.reload();
            return;
          }
          setLastSyncedAt(cloud.serverUpdatedAt ?? null);
          setStatus('synced');
        } else if (!cloud.exists) {
          // 雲端還沒資料 → 用本機建立第一份
          await pushNow('initial-seed');
        } else {
          baseServerUpdatedAt.current = cloud.serverUpdatedAt ?? baseServerUpdatedAt.current;
          setLastSyncedAt(cloud.serverUpdatedAt ?? null);
          setStatus('synced');
        }
        lastPushed.current = snapshotString();
        hydrated.current = true;
      } catch (err) {
        console.warn('[prefs-sync] initial fetch failed', err);
        setStatus('error');
        hydrated.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncId]);

  // 變更偵測：每 2 秒比對快照，有變就 debounce 推上雲。
  // 注意：不可在這裡提前改 lastPushed.current —— 它必須維持「上次成功推送」基準，
  // 供 409 衝突時的欄位級合併判斷哪些 key 是本機改的。改用 scheduledFor 去重排程。
  useEffect(() => {
    if (!syncId) return;
    const interval = setInterval(() => {
      if (!hydrated.current) return;
      const snapshot = snapshotString();
      if (snapshot === lastPushed.current) return;
      if (snapshot === scheduledFor.current) return; // 同一份已排程，不重複
      scheduledFor.current = snapshot;
      if (pushTimer.current) clearTimeout(pushTimer.current);
      pushTimer.current = setTimeout(() => { void pushNow('local-change'); }, 1500);
    }, 2000);
    return () => {
      clearInterval(interval);
      if (pushTimer.current) clearTimeout(pushTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncId]);

  // 遠端輪詢：每 8 秒對「沒有待推本機變更」的 idle 裝置拉雲端，套用別台的變更。
  useEffect(() => {
    if (!syncId) return;
    const interval = setInterval(() => {
      if (!hydrated.current || inFlight.current) return;
      const snapshot = snapshotString();
      if (snapshot !== lastPushed.current) return; // 有本機未推變更時，交給 push 流程處理衝突
      const id = getStoredSyncId();
      if (!id) return;
      void (async () => {
        try {
          const cloud = await fetchJSON<PrefsGetResp>(`/api/prefs?id=${encodeURIComponent(id)}`);
          if (!cloud.exists || !cloud.payload) return;
          if (cloud.serverUpdatedAt === baseServerUpdatedAt.current) return; // 沒有別台新版本
          baseServerUpdatedAt.current = cloud.serverUpdatedAt ?? null;
          writeLocalStorage(LOCAL_UPDATED_AT_KEY, cloud.serverUpdatedAt ?? '');
          const local = snapshotPayload();
          const changed = applyPayload(cloud.payload);
          lastPushed.current = snapshotString();
          setLastSyncedAt(cloud.serverUpdatedAt ?? null);
          setStatus('synced');
          // 只有實際改到本機 render-affecting key 才 reload 一次
          if (changed && payloadsDiffer(local, cloud.payload as PrefsValues, SYNCED_KEYS)) {
            reloadOnce();
          }
        } catch (err) {
          console.warn('[prefs-sync] remote poll failed', err);
        }
      })();
    }, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncId]);

  function enableSync() {
    let id = getStoredSyncId();
    if (!id) {
      id = generateSyncId();
      writeLocalStorage(SYNC_ID_KEY, id);
    }
    setSyncId(id);
    setStatus('syncing');
  }

  function applySyncId(id: string): boolean {
    const trimmed = id.trim();
    if (!SYNC_ID_RE.test(trimmed)) return false;
    writeLocalStorage(SYNC_ID_KEY, trimmed);
    // 強制把雲端視為較新：清掉本機 updatedAt / base 並允許 reload
    baseServerUpdatedAt.current = null;
    try { localStorage.removeItem(LOCAL_UPDATED_AT_KEY); } catch {/**/}
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch {/**/}
    window.location.reload();
    return true;
  }

  return { syncId, status, lastSyncedAt, applySyncId, enableSync };
}
